import * as Sentry from '@sentry/browser';

const INSTAGRAM_BASE_URL = 'https://graph.instagram.com/';

export interface InstagramPost {
  id: string;
  media_type: string;
  media_url: string;
  caption: string;
  thumbnail_url?: string;
  permalink: string;
}

export interface MediaEndpointParams {
  limit?: number;
  fields?: string;
  access_token: string;
}

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export async function executeRequest<T>(
  url: string,
  method: HttpMethod,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: Record<string, any>
): Promise<T> {
  let apiUrl = url;

  if (method === HttpMethod.GET && params) {
    apiUrl += `?${new URLSearchParams(params).toString()}`;
  }

  const config: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (method !== HttpMethod.GET && params) {
    config.body = JSON.stringify(params);
  }

  try {
    const response: Response = await fetch(apiUrl, config);

    if (!response.ok) {
      throw new Error(`Instagram Api: Error fetching data. Status: ${response.status}`);
    }

    const responseData: T = (await response.json()) as T;

    return responseData;
  } catch (err) {
    Sentry.captureException(`Instagram Api: Error fetching data`);
    throw new Error('Instagram Api: Error fetching data');
  }
}

export const getInstagramPosts = async (
  userId: string,
  params: MediaEndpointParams
): Promise<InstagramPost[]> => {
  const apiUrl = `${INSTAGRAM_BASE_URL}${userId}/media`;

  try {
    const { data } = await executeRequest<{ data: InstagramPost[] }>(
      apiUrl,
      HttpMethod.GET,
      params
    );
    return data || [];
  } catch (err) {
    Sentry.captureException(`Instagram Api: Error fetching Instagram posts`);
    return [];
  }
};
